/**
 * English message catalogue — the source of truth for the key set.
 *
 * `MessageKey` is derived from this object, so adding a key here immediately
 * makes every other catalogue fail to type-check until it is translated too.
 *
 * Conventions:
 * - Keys are flat and dot-namespaced; the namespace mirrors the UI surface.
 * - Placeholders use `{name}`; write `{{` for a literal brace.
 * - The product name is never hardcoded — callers pass `{product}` from
 *   `BRANDING.name` so a rebrand stays a one-file change.
 * - Pseudocode keywords stay English in this version. We translate the
 *   interface, not the language, so a flow written in Brazil still parses
 *   everywhere. Keyword text quoted inside a message is therefore literal.
 */
export const en = {
  /* ---------------------------------------------------------------- *
   * Application shell
   * ---------------------------------------------------------------- */
  'app.name': '{product}',
  'app.tagline': 'Write the logic. Watch the flowchart.',
  'app.description':
    'Write pseudocode on the left and watch the flowchart build itself on the right. Everything runs in your browser.',
  'app.documentTitle': '{name} — {product}',
  'app.welcomeTitle': 'Welcome to {product}',
  'app.welcomeBody':
    'Describe your logic in plain steps and the diagram keeps up as you type. Start from an example or write your own.',
  'app.getStarted': 'Get started',
  'app.privacyNote': 'Everything stays on this device. No account, no server, no upload.',
  'app.loading': 'Loading {product}…',
  'app.offlineReady': '{product} is ready to work offline.',
  'app.updateAvailable': 'A new version of {product} is available.',
  'app.reload': 'Reload',
  'app.errorTitle': 'Something went wrong',
  'app.errorBody':
    '{product} hit an unexpected error. Your work is stored on this device, so reloading is usually safe.',
  'app.errorDetails': 'Technical details',
  'app.errorReset': 'Reload the app',
  'app.version': 'Version {version}',

  /* ---------------------------------------------------------------- *
   * Toolbar
   * ---------------------------------------------------------------- */
  'toolbar.new': 'New',
  'toolbar.open': 'Open',
  'toolbar.import': 'Import',
  'toolbar.save': 'Save',
  'toolbar.export': 'Export',
  'toolbar.undo': 'Undo',
  'toolbar.redo': 'Redo',
  'toolbar.format': 'Format',
  'toolbar.relayout': 'Re-layout',
  'toolbar.theme': 'Theme',
  'toolbar.settings': 'Settings',
  'toolbar.help': 'Help',
  'toolbar.projects': 'Projects',
  'toolbar.examples': 'Examples',
  'toolbar.share': 'Share',
  'toolbar.menu': 'Menu',
  'toolbar.more': 'More actions',
  'toolbar.label': 'Main actions',

  /* ---------------------------------------------------------------- *
   * Panels
   * ---------------------------------------------------------------- */
  'panel.code': 'Code',
  'panel.diagram': 'Diagram',
  'panel.split': 'Split',
  'panel.swapPanels': 'Swap panels',
  'panel.codeOnly': 'Code only',
  'panel.diagramOnly': 'Diagram only',
  'panel.problems': 'Problems',
  'panel.outline': 'Outline',
  'panel.inspector': 'Inspector',
  'panel.collapse': 'Collapse panel',
  'panel.expand': 'Expand panel',
  'panel.resizeHandle': 'Resize panels',
  'panel.inspectorEmpty': 'Select a node to inspect it.',

  /* ---------------------------------------------------------------- *
   * Editor
   * ---------------------------------------------------------------- */
  // Braces are doubled because every message goes through `interpolate`.
  'editor.placeholder': 'start;\nDo something;\nif (Ready?) {{ Ship it; }}\nend;',
  'editor.lineCol': 'Line {line}, column {column}',
  'editor.autoUpdate': 'Update as I type',
  'editor.manualUpdate': 'Update manually',
  'editor.updateNow': 'Update now',
  'editor.formatDocument': 'Format document',
  'editor.pendingChanges': 'The diagram is waiting for your changes.',
  'editor.characters': '{count} characters',
  'editor.lines': '{count} lines',
  'editor.label': 'Pseudocode editor',

  /* ---------------------------------------------------------------- *
   * Diagram
   * ---------------------------------------------------------------- */
  'diagram.zoomIn': 'Zoom in',
  'diagram.zoomOut': 'Zoom out',
  'diagram.fitToScreen': 'Fit to screen',
  'diagram.resetZoom': 'Reset zoom',
  'diagram.zoomLevel': '{percent}%',
  'diagram.minimap': 'Minimap',
  'diagram.grid': 'Grid',
  'diagram.direction': 'Flow direction',
  'diagram.directionTopToBottom': 'Top to bottom',
  'diagram.directionLeftToRight': 'Left to right',
  'diagram.expandFunctions': 'Expand functions',
  'diagram.collapseFunctions': 'Collapse functions',
  'diagram.emptyTitle': 'Nothing to draw yet',
  'diagram.emptyBody': 'Write a step on the left and it shows up here.',
  'diagram.staleWarning': 'Showing the last diagram that compiled. Fix the errors to refresh it.',
  'diagram.truncatedWarning':
    'The diagram was cut short at {count} nodes. Collapse functions or split the flow to see the rest.',
  'diagram.nodeCount': '{count} nodes',
  'diagram.edgeCount': '{count} connections',
  'diagram.label': 'Flowchart',

  /* ---------------------------------------------------------------- *
   * Problems
   * ---------------------------------------------------------------- */
  'problems.title': 'Problems',
  'problems.none': 'No problems found.',
  'problems.errors': '{count} errors',
  'problems.warnings': '{count} warnings',
  'problems.infos': '{count} notes',
  'problems.summary': '{errors} errors, {warnings} warnings',
  'problems.goToLine': 'Go to line {line}',
  'problems.filterAll': 'All',
  'problems.filterErrors': 'Errors',
  'problems.filterWarnings': 'Warnings',
  'problems.filterInfos': 'Notes',
  'problems.location': 'Line {line}, column {column}',

  /* ---------------------------------------------------------------- *
   * Settings
   * ---------------------------------------------------------------- */
  'settings.title': 'Settings',
  'settings.appearance': 'Appearance',
  'settings.theme': 'Theme',
  'settings.themeSystem': 'Match system',
  'settings.themeLight': 'Light',
  'settings.themeDark': 'Dark',
  'settings.language': 'Language',
  'settings.languageSystem': 'Match system',
  'settings.languageEn': 'English',
  'settings.languagePt': 'Português (Brasil)',
  'settings.languageNote': 'The interface is translated; pseudocode keywords stay in English.',
  'settings.editor': 'Editor',
  'settings.editorFont': 'Editor font',
  'settings.fontSize': 'Font size',
  'settings.fontSizeValue': '{size} px',
  'settings.debounce': 'Update delay',
  'settings.debounceValue': '{ms} ms',
  'settings.debounceHint': 'How long to wait after you stop typing before redrawing.',
  'settings.layout': 'Layout',
  'settings.spacing': 'Node spacing',
  'settings.spacingHint': 'Space between rows and columns of the diagram.',
  'settings.direction': 'Flow direction',
  'settings.branchLabels': 'Branch labels',
  'settings.yesLabel': 'Label for the true branch',
  'settings.noLabel': 'Label for the false branch',
  'settings.showGrid': 'Show grid',
  'settings.reduceMotion': 'Reduce motion',
  'settings.reduceMotionHint': 'Turns off diagram animations and transitions.',
  'settings.nodeColors': 'Colour nodes by type',
  'settings.reset': 'Reset to defaults',
  'settings.resetConfirm': 'Reset every setting to its default value?',

  /* ---------------------------------------------------------------- *
   * Projects
   * ---------------------------------------------------------------- */
  'projects.title': 'Projects',
  'projects.create': 'New project',
  'projects.rename': 'Rename',
  'projects.duplicate': 'Duplicate',
  'projects.delete': 'Delete',
  'projects.deleteConfirm': 'Delete "{name}"? This cannot be undone.',
  'projects.search': 'Search projects',
  'projects.empty': 'No projects yet.',
  'projects.emptyHint': 'Create one, or load an example to start from.',
  'projects.noMatches': 'No project matches "{query}".',
  'projects.lastEdited': 'Edited {date}',
  'projects.untitled': 'Untitled',
  'projects.open': 'Open',
  'projects.saving': 'Saving…',
  'projects.saved': 'Saved',
  'projects.saveError': 'Could not save. Your latest changes are only in this tab.',
  'projects.quotaExceeded':
    'Storage is full. Delete a project or export it to a file to free up space.',
  'projects.nameLabel': 'Project name',
  'projects.copySuffix': '{name} (copy)',
  'projects.count': '{count} projects',

  /* ---------------------------------------------------------------- *
   * Examples
   * ---------------------------------------------------------------- */
  'examples.title': 'Examples',
  'examples.subtitle': 'Ready-made flows to read, run and take apart.',
  'examples.load': 'Load',
  'examples.loadConfirmTitle': 'Replace the current document?',
  'examples.loadConfirmBody':
    'Loading "{name}" replaces what is in the editor. Unsaved changes are lost.',
  'examples.cancel': 'Keep my work',
  'examples.empty': 'No examples in this category.',
  'examples.category.all': 'All',
  'examples.category.basics': 'Basics',
  'examples.category.controlFlow': 'Control flow',
  'examples.category.functions': 'Functions',
  'examples.category.patterns': 'Patterns',
  'examples.category.algorithms': 'Algorithms',

  /* ---------------------------------------------------------------- *
   * Export
   * ---------------------------------------------------------------- */
  'export.title': 'Export',
  'export.format': 'Format',
  'export.code': 'Pseudocode',
  'export.json': 'JSON document',
  'export.svg': 'SVG image',
  'export.png': 'PNG image',
  'export.fileName': 'File name',
  'export.scale': 'Scale',
  'export.scaleValue': '{scale}×',
  'export.margin': 'Margin',
  'export.marginValue': '{margin} px',
  'export.transparentBackground': 'Transparent background',
  'export.includeBackground': 'Include background',
  'export.visibleAreaOnly': 'Visible area only',
  'export.wholeDiagram': 'Whole diagram',
  'export.download': 'Download',
  'export.copyLink': 'Copy link',
  'export.linkCopied': 'Link copied to the clipboard.',
  'export.linkTooLong':
    'This document is too long to fit in a link. Export a file and share that instead.',
  'export.done': 'Exported {fileName}.',
  'export.failed': 'Export failed. Try a smaller scale or a different format.',

  /* ---------------------------------------------------------------- *
   * Import
   * ---------------------------------------------------------------- */
  'import.title': 'Import',
  'import.dropHere': 'Drop a file here',
  'import.chooseFile': 'Choose a file',
  'import.accepted': 'Accepts {extensions}',
  'import.reading': 'Reading {name}…',
  'import.invalidFile': 'That file is not a {product} document.',
  'import.unsupportedVersion':
    'This file was written by a newer version ({version}). Update {product} and try again.',
  'import.importAsNew': 'Import as a new project',
  'import.replaceCurrent': 'Replace the current document',
  'import.tooLarge': 'That file is {size}, over the {limit} limit.',
  'import.done': 'Imported {name}.',

  /* ---------------------------------------------------------------- *
   * Accessibility
   * ---------------------------------------------------------------- */
  'a11y.skipToEditor': 'Skip to the editor',
  'a11y.skipToDiagram': 'Skip to the diagram',
  'a11y.skipToProblems': 'Skip to the problems list',
  'a11y.landmarkBanner': 'Application header',
  'a11y.landmarkToolbar': 'Toolbar',
  'a11y.landmarkMain': 'Editor and diagram',
  'a11y.landmarkEditor': 'Pseudocode editor',
  'a11y.landmarkDiagram': 'Flowchart',
  'a11y.landmarkProblems': 'Problems',
  'a11y.landmarkOutline': 'Diagram outline',
  'a11y.landmarkInspector': 'Node inspector',
  'a11y.landmarkStatus': 'Status bar',
  'a11y.outlineTitle': 'Diagram outline',
  'a11y.outlineHint': 'A reading order of the diagram. Choose an item to reveal it in the code.',
  'a11y.outlineEmpty': 'The diagram is empty.',
  'a11y.outlineItem': '{role}: {label}',
  'a11y.outlineLevel': 'Level {depth}',
  'a11y.outlineBranch': 'Branch {label}',
  'a11y.outlineFunction': 'Function {name}',
  'a11y.outlineLeadsTo': 'Leads to {label}',
  'a11y.outlineLoopsBackTo': 'Loops back to {label}',
  'a11y.diagramUpdated': 'Diagram updated, {nodes} nodes',
  'a11y.diagramEmpty': 'Diagram is empty',
  'a11y.diagramUnchanged': 'Diagram unchanged',
  'a11y.problemsAnnounce': '{errors} errors and {warnings} warnings',
  'a11y.problemsCleared': 'All problems resolved',
  'a11y.nodeSelected': 'Selected {label}',
  'a11y.nodeDeselected': 'Selection cleared',
  'a11y.zoomAnnounce': 'Zoom {percent} percent',
  'a11y.savedAnnounce': 'Project saved',
  'a11y.themeChanged': 'Theme changed to {theme}',
  'a11y.languageChanged': 'Language changed to {language}',
  'a11y.role.start': 'Start',
  'a11y.role.end': 'End',
  'a11y.role.action': 'Step',
  'a11y.role.decision': 'Decision',
  'a11y.role.loop': 'Loop',
  'a11y.role.call': 'Function call',
  'a11y.role.io': 'Input or output',
  'a11y.role.junction': 'Junction',
  'a11y.role.note': 'Note',
  'a11y.role.return': 'Return',
  // Suffixes match `NodeRole` verbatim so `a11y.role.${node.role}` resolves.
  'a11y.role.function-entry': 'Function start',
  'a11y.role.function-exit': 'Function end',
  'a11y.role.error': 'Error',

  /* ---------------------------------------------------------------- *
   * Keyboard shortcuts
   * ---------------------------------------------------------------- */
  'shortcuts.title': 'Keyboard shortcuts',
  'shortcuts.groupGeneral': 'General',
  'shortcuts.groupEditor': 'Editor',
  'shortcuts.groupDiagram': 'Diagram',
  'shortcuts.groupNavigation': 'Navigation',
  'shortcuts.newProject': 'New project',
  'shortcuts.openProjects': 'Open the project list',
  'shortcuts.save': 'Save the project',
  'shortcuts.import': 'Import a file',
  'shortcuts.export': 'Open the export dialog',
  'shortcuts.undo': 'Undo',
  'shortcuts.redo': 'Redo',
  'shortcuts.formatDocument': 'Format the document',
  'shortcuts.updateNow': 'Update the diagram now',
  'shortcuts.find': 'Find in the code',
  'shortcuts.toggleComment': 'Comment or uncomment the line',
  'shortcuts.focusEditor': 'Move focus to the editor',
  'shortcuts.focusDiagram': 'Move focus to the diagram',
  'shortcuts.focusProblems': 'Move focus to the problems list',
  'shortcuts.nextProblem': 'Go to the next problem',
  'shortcuts.previousProblem': 'Go to the previous problem',
  'shortcuts.zoomIn': 'Zoom in',
  'shortcuts.zoomOut': 'Zoom out',
  'shortcuts.fitToScreen': 'Fit the diagram to the screen',
  'shortcuts.resetZoom': 'Reset the zoom',
  'shortcuts.toggleMinimap': 'Show or hide the minimap',
  'shortcuts.toggleGrid': 'Show or hide the grid',
  'shortcuts.swapPanels': 'Swap the panels',
  'shortcuts.togglePanelLayout': 'Cycle the panel layout',
  'shortcuts.toggleTheme': 'Switch between light and dark',
  'shortcuts.openSettings': 'Open settings',
  'shortcuts.openHelp': 'Open the shortcut list',
  'shortcuts.closeDialog': 'Close the dialog',

  /* ---------------------------------------------------------------- *
   * Shared vocabulary
   * ---------------------------------------------------------------- */
  'common.ok': 'OK',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.confirm': 'Confirm',
  'common.delete': 'Delete',
  'common.rename': 'Rename',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.retry': 'Try again',
  'common.dismiss': 'Dismiss',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.loading': 'Loading…',
  'common.error': 'Error',
  'common.save': 'Save',
  'common.apply': 'Apply',
  'common.done': 'Done',
  'common.back': 'Back',
  'common.search': 'Search',
  'common.none': 'None',

  /* ---------------------------------------------------------------- *
   * Diagnostics
   *
   * One message and one hint per code in `language/diagnostics/types.ts`.
   * Placeholders match the `params` the reporting pass attaches.
   * ---------------------------------------------------------------- */
  'diagnostics.LEX001': 'This text is not closed before the end of the line.',
  'diagnostics.LEX001.hint': 'Add the missing closing quote.',
  'diagnostics.LEX002': 'The comment opened on line {line} is never closed.',
  'diagnostics.LEX002.hint': 'Close it with {close}.',
  'diagnostics.LEX003': 'Unexpected character "{character}".',
  'diagnostics.LEX003.hint': 'Remove it, or put it inside a label.',

  'diagnostics.SYN001': 'The block opened on line {line} is never closed.',
  'diagnostics.SYN001.hint': 'Add a closing brace for the block opened on line {line}.',
  'diagnostics.SYN002': 'This closing brace has no matching opening brace.',
  'diagnostics.SYN002.hint': 'Remove it, or open a block before it.',
  'diagnostics.SYN003': 'The parenthesis opened on line {line} is never closed.',
  'diagnostics.SYN003.hint': 'Add the closing parenthesis.',
  'diagnostics.SYN004': 'This statement does not end with a semicolon.',
  'diagnostics.SYN004.hint': 'Add a semicolon at the end of line {line}.',
  'diagnostics.SYN005': '"else" has no matching "if".',
  'diagnostics.SYN005.hint': 'Add an "if" block before it, or delete the "else".',
  'diagnostics.SYN006': '"case" only belongs inside a "switch" block.',
  'diagnostics.SYN006.hint': 'Move this case into a "switch" block.',
  'diagnostics.SYN007': '"{keyword}" needs a condition in parentheses.',
  'diagnostics.SYN007.hint': 'Write the condition between parentheses, for example (Ready?).',
  'diagnostics.SYN008': '"{keyword}" needs a block in braces.',
  'diagnostics.SYN008.hint': 'Wrap the steps that belong to it in braces.',
  'diagnostics.SYN009': 'This function has no name.',
  'diagnostics.SYN009.hint': 'Write "function" followed by a name, then the body in braces.',
  'diagnostics.SYN010': '"call" needs the name of a function.',
  'diagnostics.SYN010.hint': 'Write "call" followed by the function name.',
  'diagnostics.SYN011': 'A "repeat" block must end with "until".',
  'diagnostics.SYN011.hint': 'Add "until" and a condition after the closing brace.',
  'diagnostics.SYN012': 'Unexpected "{found}", expected {expected}.',
  'diagnostics.SYN012.hint': 'Check the syntax around line {line}.',
  'diagnostics.SYN013': 'This case label does not end with a colon.',
  'diagnostics.SYN013.hint': 'Add a colon after the case value.',
  'diagnostics.SYN014': 'This label is empty.',
  'diagnostics.SYN014.hint': 'Describe the step, or remove the label.',
  'diagnostics.SYN015': '"for each" needs a header such as (item in collection).',
  'diagnostics.SYN015.hint': 'Name what you loop over, for example (Order in orders).',
  'diagnostics.SYN016': '"default" only belongs inside a "switch" block.',
  'diagnostics.SYN016.hint': 'Move this default clause into a "switch" block.',

  'diagnostics.SEM001': 'Function "{name}" is already declared on line {line}.',
  'diagnostics.SEM001.hint': 'Rename this one, or remove the earlier declaration.',
  'diagnostics.SEM002': 'No function named "{name}" is declared.',
  'diagnostics.SEM002.hint': 'Declare it, or check the spelling.',
  'diagnostics.SEM003': '"break" only means something inside a loop.',
  'diagnostics.SEM003.hint': 'Move it into a loop, or remove it.',
  'diagnostics.SEM004': '"continue" only means something inside a loop.',
  'diagnostics.SEM004.hint': 'Move it into a loop, or remove it.',
  'diagnostics.SEM005': '"return" only means something inside a function.',
  'diagnostics.SEM005.hint': 'Use "end" to finish the main flow.',
  'diagnostics.SEM006': 'The flow always stops before this step, so it never runs.',
  'diagnostics.SEM006.hint': 'Move it above line {line}, or remove it.',
  'diagnostics.SEM007': 'This block is empty.',
  'diagnostics.SEM007.hint': 'Add a step, or remove the block.',
  'diagnostics.SEM008': 'This branch label is empty.',
  'diagnostics.SEM008.hint': 'Write a label, or drop it to use the default one.',
  'diagnostics.SEM009': 'Function "{name}" calls itself.',
  'diagnostics.SEM009.hint': 'Recursive calls are drawn collapsed to keep the diagram finite.',
  'diagnostics.SEM010': 'Function "{name}" is part of a call cycle: {cycle}.',
  'diagnostics.SEM010.hint': 'Break the cycle, or leave those calls collapsed.',
  'diagnostics.SEM011': 'The id "{id}" is already used on line {line}.',
  'diagnostics.SEM011.hint': 'Give this node an id of its own.',
  'diagnostics.SEM012': 'Function "{name}" is never called.',
  'diagnostics.SEM012.hint': 'Call it from the flow, or remove the declaration.',
  'diagnostics.SEM013': 'Unknown annotation "{name}".',
  'diagnostics.SEM013.hint': 'Supported annotations: {supported}.',
  'diagnostics.SEM014': '"{value}" is not a valid value for the "{name}" annotation.',
  'diagnostics.SEM014.hint': 'Expected {expected}.',
  'diagnostics.SEM015': 'This switch already has a default clause on line {line}.',
  'diagnostics.SEM015.hint': 'Keep one default clause and remove the rest.',
  'diagnostics.SEM016': 'This switch has no cases.',
  'diagnostics.SEM016.hint': 'Add a case, or replace the switch with a plain step.',
  'diagnostics.SEM017': 'Steps written after "end" are never reached.',
  'diagnostics.SEM017.hint': 'Move them above the "end" on line {line}.',
  'diagnostics.SEM018': 'The flow has {count} start points.',
  'diagnostics.SEM018.hint': 'Keep one start; the next one is on line {line}.',
  'diagnostics.SEM019': 'The flow has no explicit start.',
  'diagnostics.SEM019.hint': 'Add "start" as the first step.',

  'diagnostics.LIM001': 'The document has {size} characters, over the limit of {limit}.',
  'diagnostics.LIM001.hint': 'Split it into functions, or into separate projects.',
  'diagnostics.LIM002': 'Blocks are nested {depth} levels deep, over the limit of {limit}.',
  'diagnostics.LIM002.hint': 'Move the inner blocks into functions.',
  'diagnostics.LIM003': 'The diagram would have {count} nodes, over the limit of {limit}.',
  'diagnostics.LIM003.hint': 'Collapse functions, or split the flow into projects.',
  'diagnostics.LIM004': 'Function expansion stopped at depth {depth}.',
  'diagnostics.LIM004.hint': 'Collapse some functions to see the whole flow.',
};
